import { Injectable, signal, computed, inject } from '@angular/core';
import { Observable, of, throwError, delay } from 'rxjs';
import { TokenStorageService } from './token-storage.service';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const MOCK_USERS: Record<string, { password: string; user: AuthUser }> = {
  'owner@pharmacin.com': {
    password: 'password',
    user: {
      id: 'usr_001',
      email: 'owner@pharmacin.com',
      firstName: 'Chidera',
      lastName: 'Nwankwo',
      role: 'owner',
    },
  },
  'admin@pharmacin.com': {
    password: 'password',
    user: {
      id: 'usr_002',
      email: 'admin@pharmacin.com',
      firstName: 'Tunde',
      lastName: 'Adebayo',
      role: 'admin',
    },
  },
  'staff@pharmacin.com': {
    password: 'password',
    user: {
      id: 'usr_003',
      email: 'staff@pharmacin.com',
      firstName: 'Amina',
      lastName: 'Bello',
      role: 'staff',
    },
  },
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenStorage = inject(TokenStorageService);

  private _user = signal<AuthUser | null>(null);
  private _isPending = signal(false);

  user = this._user.asReadonly();
  isAuthenticated = computed(() => !!this._user());
  isPending = this._isPending.asReadonly();

  login(email: string, password: string): Observable<{ token: string; user: AuthUser }> {
    this._isPending.set(true);
    const entry = MOCK_USERS[email.toLowerCase().trim()];

    if (!entry || entry.password !== password) {
      this._isPending.set(false);
      return throwError(() => ({
        error: { message: 'Invalid email or password' },
      })).pipe(delay(600));
    }

    const token = 'mock_jwt_' + btoa(entry.user.id + ':' + Date.now());
    const user = entry.user;

    this.tokenStorage.saveToken(token);
    this._user.set(user);
    this._isPending.set(false);

    return of({ token, user }).pipe(delay(600));
  }

  logout(): void {
    this.tokenStorage.removeToken();
    this._user.set(null);
  }

  loadUser(): void {
    const token = this.tokenStorage.getAccessToken();
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.replace('mock_jwt_', '')));
      const entry = Object.values(MOCK_USERS).find((e) => e.user.id === payload.id);
      if (entry) {
        this._user.set(entry.user);
      } else {
        this.tokenStorage.removeToken();
      }
    } catch {
      this.tokenStorage.removeToken();
    }
  }
}
